import { useMemo, useState } from 'react';
import { useFeedbackList, useReplyFeedback } from '@/hooks/useFeedback';
import { Feedback } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { Search, Eye, MessageSquareReply } from 'lucide-react';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('id-ID');
};

export function FeedbackList() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [replyDraft, setReplyDraft] = useState('');

  const { data, isLoading } = useFeedbackList({ page, limit: 10, search });
  const replyFeedback = useReplyFeedback();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const totalPage = useMemo(() => Math.max(1, Math.ceil((data?.total || 0) / 10)), [data?.total]);

  const openDetail = (item: Feedback) => {
    setSelected(item);
    setReplyDraft(item.contentBalas || '');
  };

  const handleReply = async () => {
    if (!selected?.id) {
      return;
    }

    await replyFeedback.mutateAsync({
      id: selected.id,
      data: {
        content_balas: replyDraft,
      },
    });

    setSelected((current) => (current ? { ...current, contentBalas: replyDraft, isRead: true, isActive: true } : current));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Umpan Balik</h2>
        <p className="text-muted-foreground">Lihat, balas, dan kelola umpan balik pengguna.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Cari isi umpan balik..."
              className="pl-8"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="umpan balik"
            onClear={selection.clearSelection}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Isi</TableHead>
                <TableHead>Status Balasan</TableHead>
                <TableHead>Dibuat</TableHead>
                <TableHead className="w-20">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">Memuat...</TableCell>
                </TableRow>
              ) : data?.data.length ? (
                data.data.map((item) => (
                  <TableRow key={item.id} className={selection.isSelected(item.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(item.id)} onCheckedChange={(checked) => selection.toggleOne(item.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell className="font-medium">{item.kmjUserId || '-'}</TableCell>
                    <TableCell className="max-w-[420px] truncate">{item.content || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={item.contentBalas ? 'default' : 'secondary'}>
                        {item.contentBalas ? 'Sudah Dibalas' : 'Belum Dibalas'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="icon" onClick={() => openDetail(item)}>
                        {item.contentBalas ? <Eye className="h-4 w-4" /> : <MessageSquareReply className="h-4 w-4" />}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">Belum ada data umpan balik.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Sebelumnya
        </Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {totalPage}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPage} onClick={() => setPage((p) => p + 1)}>
          Selanjutnya
        </Button>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detail Umpan Balik</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div><span className="font-semibold">User ID:</span> {selected?.kmjUserId || '-'}</div>
            <div><span className="font-semibold">Isi:</span> {selected?.content || '-'}</div>
            <div><span className="font-semibold">Dibuat:</span> {formatDate(selected?.createdAt)}</div>
            <div className="space-y-2">
              <span className="font-semibold">Balasan:</span>
              <Textarea
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder="Tulis balasan untuk feedback ini..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Tutup</Button>
            <Button onClick={handleReply} disabled={replyFeedback.isPending || !replyDraft.trim()}>
              {replyFeedback.isPending ? 'Menyimpan...' : 'Simpan Balasan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
